import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, FlaskConical, Hash, Users } from '@digital-twins/design-system/icons'
import { LibraryCollectionCard, LibraryCollectionCover, LibraryItemKind } from '@digital-twins/shared-types'

import { CollectionSharingBadge } from '@/components/library/CollectionSharingBadge'
import { LibraryCountPhrase } from '@/components/library/LibraryCountPhrase'
import {
  LIBRARY_CARD_CHIP_CLASS,
  LIBRARY_CARD_COVER_CHIP_CLASS,
  LIBRARY_CARD_COVER_CLASS,
  LIBRARY_CARD_META_CLASS,
  LIBRARY_CARD_TITLE_CLASS,
} from '@/components/library/libraryCardStyles'
import { FadeInImage, coverImageAnchorClass } from '@/components/library/LibraryItemCover'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import Link from '@/routing/Link'
import { ROUTES } from '@/routes/routes'

export interface MosaicCollectionCardProps {
  collection: LibraryCollectionCard
  /** Test ids are `library-collection-card-{index}`. */
  index: number
}

const COVER_ICONS: Record<LibraryItemKind, typeof FileText> = {
  document: FileText,
  dataset: FileText,
  study: FlaskConical,
  audience: Users,
  topic: Hash,
}

/** The mosaic always draws four tiles. */
const MOSAIC_TILES = 4

interface CoverTileProps {
  cover: LibraryCollectionCover | undefined
  kindLabel?: string
}

/**
 * One mosaic tile: the primary tint with the kind's icon, and the cover
 * image fading in over it once loaded, so a tile is never an empty slab
 * (an image that fails leaves the tint).
 */
function CoverTile(props: CoverTileProps) {
  const { cover, kindLabel } = props

  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  const Icon = cover ? COVER_ICONS[cover.kind] : null
  const imageUrl = cover?.imageUrl && cover.imageUrl !== failedSrc ? cover.imageUrl : null

  return (
    <span className="relative block h-full w-full overflow-hidden bg-primary/10">
      <span className="absolute inset-0 flex items-center justify-center text-primary/60" aria-hidden="true">
        {Icon ? <Icon className="h-5 w-5" /> : null}
      </span>
      {imageUrl && cover ? (
        <FadeInImage
          src={imageUrl}
          className={cn(
            'relative h-full w-full object-cover transition-[transform,opacity] duration-300 group-hover:scale-[1.02]',
            coverImageAnchorClass(cover.kind === 'document' || cover.kind === 'dataset'),
          )}
          onError={() => setFailedSrc(imageUrl)}
        />
      ) : null}
      {kindLabel ? (
        <span className="absolute inset-x-2 bottom-2 flex min-w-0">
          <Badge className={cn(LIBRARY_CARD_CHIP_CLASS, LIBRARY_CARD_COVER_CHIP_CLASS, 'max-w-full')} title={kindLabel}>
            <span className="truncate">{kindLabel}</span>
          </Badge>
        </span>
      ) : null}
    </span>
  )
}

export interface CollectionCoverMosaicProps {
  cover: Array<LibraryCollectionCover>
  /** Set on the tile grid, whose children are the four tiles. */
  testId: string
  className?: string
  /** Laid over the cover's bottom edge (chips). */
  overlay?: ReactNode
  /** Resource kinds on individual tiles, used by the featured collection. */
  showKindLabels?: boolean
}

/**
 * A 2 by 2 mosaic of a collection's first four covers in the Library card's
 * rounded frame. A missing cover is a primary tint (never grey) with the
 * item kind's icon when the kind is known.
 */
export function CollectionCoverMosaic(props: CollectionCoverMosaicProps) {
  const { cover, testId, className, overlay, showKindLabels = false } = props
  const { t } = useTranslation('app')

  const tiles = Array.from({ length: MOSAIC_TILES }, (_, position) => cover[position])

  return (
    // The frame's own grey would show between the tiles: the tint replaces it.
    <div className={cn(LIBRARY_CARD_COVER_CLASS, 'bg-primary/5', className)}>
      <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-0.5" data-testid={testId}>
        {tiles.map((tile, position) => (
          <CoverTile key={position} cover={tile} kindLabel={showKindLabels && tile ? t(`library.collections.resources.kindSingular.${tile.kind}`) : undefined} />
        ))}
      </div>
      {overlay ? (
        <div className="absolute inset-x-3 bottom-3 flex min-w-0 items-center gap-1.5 overflow-hidden">{overlay}</div>
      ) : null}
    </div>
  )
}

/**
 * A collection in the Library card's magazine language: no box, the cover
 * mosaic carries the frame with the sharing chip over it, then the Sumana
 * name and the dot separated counts. The whole card is one link to the
 * collection.
 */
export function MosaicCollectionCard(props: MosaicCollectionCardProps) {
  const { collection, index } = props

  const { t } = useTranslation('app')

  const testId = `library-collection-card-${index}`
  const resourceTotal = Object.values(collection.resourceCounts).reduce((sum, count) => sum + count, 0)
  const hasTwins = collection.resourceCounts.audience > 0 && collection.twinsCount > 0

  const coverChips = (
    <>
      <Badge
        variant="muted"
        className={cn(LIBRARY_CARD_CHIP_CLASS, LIBRARY_CARD_COVER_CHIP_CLASS, 'shrink-0')}
        data-testid={`${testId}-sharing`}
      >
        <CollectionSharingBadge sharingMode={collection.sharingMode} showLabel className="text-inherit" />
      </Badge>
      {collection.origin === 'saved_filter' ? (
        <Badge
          variant="muted"
          className={cn(LIBRARY_CARD_CHIP_CLASS, LIBRARY_CARD_COVER_CHIP_CLASS, 'shrink-0')}
          data-testid={`${testId}-saved-filter`}
        >
          {t('library.collections.header.savedFilter')}
        </Badge>
      ) : null}
    </>
  )

  return (
    <Link
      href={ROUTES.knowledgeCollection(collection.token)}
      className="group flex h-full min-w-0 flex-col rounded-[14px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      data-testid={testId}
    >
      <CollectionCoverMosaic
        cover={collection.cover}
        testId={`${testId}-cover`}
        className="aspect-[16/10] shrink-0"
        overlay={coverChips}
      />
      <div className="flex min-w-0 flex-col gap-2 pt-3">
        <h3 className={LIBRARY_CARD_TITLE_CLASS} data-testid={`${testId}-name`}>
          {collection.name}
        </h3>
        <p className={LIBRARY_CARD_META_CLASS} data-testid={`${testId}-meta`}>
          <LibraryCountPhrase noun="resources" count={resourceTotal} />
          <span aria-hidden="true">·</span>
          <LibraryCountPhrase noun="topics" count={collection.topicCount} />
          {hasTwins ? (
            <>
              <span aria-hidden="true">·</span>
              <LibraryCountPhrase noun="twins" count={collection.twinsCount} testId={`${testId}-twins`} />
            </>
          ) : null}
        </p>
      </div>
    </Link>
  )
}
